import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, Flag, ChevronLeft, CalendarPlus, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_QUICK_REPLIES = ["On my way! 🚗", "Running 10 min late — sorry!", "I've arrived 👋", "All done — thank you! 🙌", "Can we reschedule?"];

let _audioCtx = null;
const playPing = () => {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.08);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.4);
  } catch (e) { /* audio blocked until user interacts — fine */ }
};

export const MessagesPanel = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [quickReplies, setQuickReplies] = useState(DEFAULT_QUICK_REPLIES);
  const [editingQR, setEditingQR] = useState(false);
  const [qrDraft, setQrDraft] = useState("");
  const lastTypedRef = useRef(0);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const prevUnreadRef = useRef(null);

  const sendPhoto = async (file) => {
    if (!file || !active || sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("recipient_id", active.other_id);
      fd.append("caption", text.trim());
      fd.append("photo", file);
      const { data } = await api.post("/messages/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsgs((m) => [...m, data]);
      setText("");
      loadThreads();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const loadThreads = () => api.get("/messages/threads").then(({ data }) => {
    const total = data.reduce((s, t) => s + (t.unread || 0), 0);
    if (prevUnreadRef.current !== null && total > prevUnreadRef.current) playPing();
    prevUnreadRef.current = total;
    setThreads(data);
  }).catch(() => {});
  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (user?.role === "professional") {
      api.get("/professional/quick-replies").then(({ data }) => setQuickReplies(data.replies)).catch(() => {});
    }
  }, [user?.role]);

  const saveQuickReplies = async () => {
    const replies = qrDraft.split("\n").map((r) => r.trim()).filter(Boolean).slice(0, 8);
    if (!replies.length) { toast.error("Add at least one quick reply (one per line)"); return; }
    try {
      const { data } = await api.put("/professional/quick-replies", { replies });
      setQuickReplies(data.replies);
      setEditingQR(false);
      toast.success("Quick replies saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const openThread = (t) => {
    setActive(t);
    api.get(`/messages/thread/${t.other_id}`).then(({ data }) => { setMsgs(data); loadThreads(); }).catch(() => {});
  };

  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => {
      api.get(`/messages/thread/${active.other_id}`).then(({ data }) => {
        setMsgs((prev) => {
          if (prev.length && data.length > prev.length && data[data.length - 1]?.sender_id === active.other_id) playPing();
          return data;
        });
      }).catch(() => {});
    }, 15000);
    const tt = setInterval(() => {
      api.get(`/messages/typing/${active.other_id}`).then(({ data }) => setOtherTyping(data.typing)).catch(() => {});
    }, 2500);
    return () => { clearInterval(t); clearInterval(tt); setOtherTyping(false); };
  }, [active]);

  const onTextChange = (v) => {
    setText(v);
    if (active && Date.now() - lastTypedRef.current > 2000) {
      lastTypedRef.current = Date.now();
      api.post(`/messages/typing/${active.other_id}`).catch(() => {});
    }
  };

  const send = async (override) => {
    const body = (override ?? text).trim();
    if (!body || !active || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { recipient_id: active.other_id, text: body });
      setMsgs((m) => [...m, data]);
      if (!override) setText("");
      loadThreads();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const flagMsg = async (m) => {
    const reason = window.prompt("Tell us what's wrong with this message (a Care-4U admin will review it):");
    if (reason === null) return;
    try {
      await api.post(`/messages/${m.id}/flag`, { reason });
      toast.success("Reported — a Care-4U admin will review it. Thank you 🙏");
      openThread(active);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  return (
    <section className="card-metal p-4" data-testid="messages-panel">
      <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-[#9b111e]" /> Messages
        {totalUnread > 0 && <span className="text-xs bg-[#9b111e] text-white rounded-full px-2 py-0.5" data-testid="messages-unread-badge">{totalUnread} new</span>}
      </h2>
      {!active ? (
        <div className="mt-3 space-y-2">
          {threads.length === 0 && <p className="text-sm text-slate-500">No conversations yet — messaging unlocks once you're connected through a booking.</p>}
          {threads.map((t) => (
            <button key={t.other_id} onClick={() => openThread(t)} data-testid={`msg-thread-${t.other_id}`}
              className="w-full text-left flex items-center justify-between gap-3 rounded-lg border border-slate-200 hover:border-[#9b111e] p-3 transition-colors">
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">{t.other_nickname || t.other_name} {t.other_nickname && <span className="font-normal text-slate-400">({t.other_name})</span>}</p>
                <p className="text-xs text-slate-500 truncate">{t.last_message ? t.last_message.text : "Say hi 👋"}</p>
              </div>
              {t.unread > 0 && <span className="text-xs bg-[#9b111e] text-white rounded-full px-2 py-0.5 flex-shrink-0">{t.unread}</span>}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-3">
          <button onClick={() => { setActive(null); loadThreads(); }} className="text-xs text-slate-500 flex items-center gap-1 hover:text-[#9b111e]" data-testid="msg-back-btn">
            <ChevronLeft className="w-4 h-4" /> All conversations
          </button>
          <div className="flex items-center justify-between gap-2 mt-1">
            <div className="flex items-center gap-2 min-w-0">
              <p className="font-bold truncate">{active.other_nickname || active.other_name}</p>
              {otherTyping && <span className="text-xs text-slate-400 italic animate-pulse flex-shrink-0" data-testid="typing-indicator">typing…</span>}
            </div>
            {active.other_role === "professional" && (
              <Link to={`/provider/${active.other_id}?book=1`} data-testid="book-again-btn"
                className="flex items-center gap-1.5 bg-[#9b111e] text-white text-xs font-bold rounded-full px-3 py-1.5 hover:opacity-90 transition-opacity flex-shrink-0">
                <CalendarPlus className="w-3.5 h-3.5" /> Book again
              </Link>
            )}
          </div>
          <div className="mt-2 max-h-72 overflow-y-auto space-y-2 pr-1">
            {msgs.map((m) => {
              const mine = m.sender_id !== active.other_id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[#9b111e] text-white" : "bg-slate-100 text-slate-800"}`} data-testid={`msg-${m.id}`}>
                    {m.photo_url && (
                      <a href={`${api.defaults.baseURL}${m.photo_url}`} target="_blank" rel="noreferrer">
                        <img src={`${api.defaults.baseURL}${m.photo_url}`} alt="shared" loading="lazy"
                          className="rounded-lg max-h-48 w-auto mb-1.5 border border-black/10" data-testid={`msg-photo-${m.id}`} />
                      </a>
                    )}
                    <p className={m.deleted ? "italic opacity-70" : ""}>{m.text}</p>
                    {m.admin_edited && !m.deleted && <p className="text-[10px] opacity-70">edited by admin</p>}
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className={`text-[10px] ${mine ? "text-red-100" : "text-slate-400"}`}>
                        {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </p>
                      {mine && !m.deleted && (
                        <span className={`text-[10px] font-semibold ${m.read ? "text-red-100" : "text-red-200/70"}`} data-testid={`msg-receipt-${m.id}`}>
                          {m.read ? "✓✓ Seen" : "✓ Sent"}
                        </span>
                      )}
                      {!mine && !m.deleted && !m.flagged && (
                        <button onClick={() => flagMsg(m)} title="Report this message" data-testid={`flag-msg-btn-${m.id}`}
                          className="text-slate-400 hover:text-[#9b111e] transition-colors">
                          <Flag className="w-3 h-3" />
                        </button>
                      )}
                      {m.flagged && !mine && <span className="text-[10px] text-amber-600">🚩 reported</span>}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          {user?.role === "professional" && !editingQR && (
            <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1" data-testid="quick-replies">
              {quickReplies.map((q) => (
                <button key={q} onClick={() => send(q)} disabled={sending} data-testid={`quick-reply-${q.slice(0, 8)}`}
                  className="flex-shrink-0 text-xs font-semibold border border-slate-300 rounded-full px-3 py-1.5 hover:border-[#9b111e] hover:text-[#9b111e] transition-colors disabled:opacity-50">
                  {q}
                </button>
              ))}
              <button onClick={() => { setQrDraft(quickReplies.join("\n")); setEditingQR(true); }} data-testid="edit-quick-replies-btn"
                title="Customize your quick replies"
                className="flex-shrink-0 text-xs font-semibold border border-dashed border-slate-400 text-slate-500 rounded-full px-3 py-1.5 hover:border-[#9b111e] hover:text-[#9b111e] transition-colors">
                ✎ Edit
              </button>
            </div>
          )}
          {user?.role === "professional" && editingQR && (
            <div className="mt-3 rounded-lg border border-slate-200 p-3" data-testid="quick-replies-editor">
              <p className="text-xs font-bold text-slate-600">Your quick replies — one per line (max 8, 80 chars each)</p>
              <textarea value={qrDraft} onChange={(e) => setQrDraft(e.target.value)} rows={6}
                data-testid="quick-replies-textarea"
                className="w-full mt-2 text-sm rounded-lg border border-slate-300 p-2 focus:outline-none focus:border-[#9b111e]" />
              <div className="flex gap-2 mt-2">
                <button onClick={saveQuickReplies} data-testid="save-quick-replies-btn"
                  className="bg-[#9b111e] text-white text-xs font-bold rounded-lg px-4 py-1.5">Save</button>
                <button onClick={() => setEditingQR(false)} className="text-xs text-slate-500 px-2">Cancel</button>
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <input type="file" accept="image/*" capture="environment" ref={fileRef} className="hidden"
              data-testid="msg-photo-input" onChange={(e) => sendPhoto(e.target.files?.[0])} />
            <button onClick={() => fileRef.current?.click()} disabled={sending} title="Send a photo"
              data-testid="msg-photo-btn"
              className="border border-slate-300 text-slate-500 hover:text-[#9b111e] hover:border-[#9b111e] rounded-lg px-3 disabled:opacity-50 transition-colors">
              <Camera className="w-4 h-4" />
            </button>
            <Input value={text} onChange={(e) => onTextChange(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a message…" data-testid="msg-input" />
            <button onClick={() => send()} disabled={sending || !text.trim()} data-testid="msg-send-btn"
              className="bg-[#9b111e] text-white rounded-lg px-4 disabled:opacity-50 flex items-center gap-1 text-sm font-bold">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default MessagesPanel;
